/**
 * Cart Upsell Step — asset: cart-upsell-step.js
 *
 * Flow:
 *  1. Upsell 1 opens on Checkout; Upsell 2 opens after a successful product add.
 *  2. Resolve global or product-template settings.
 *  3. Open only the UI assigned to that trigger.
 *  4. Accept  -> add selected variant -> continue checkout
 *     Decline -> open cart for Upsell 2; continue checkout for Upsell 1
 */
(function () {
  'use strict';

  var BASE_CONFIG = window.CartUpsellStepConfig || {};
  var CONFIG = BASE_CONFIG;
  if (!CONFIG || !document.querySelector('#cu-step-root')) return;

  var productDataByVariant = {
    upsell1: null,
    upsell2: null
  };
  var productRequestByVariant = {
    upsell1: null,
    upsell2: null
  };
  var selectedVariantIdByVariant = {
    upsell1: '',
    upsell2: ''
  };
  var activeUiVariant = 'upsell1';

  var refs = {};
  var hasBoundEvents = false;
  var hideTimer = null;
  var offerTimerInterval = null;
  var offerBusy = false;
  var activeAddConfig = null;
  var previousFocus = null;
  var drawerWasOpen = false;
  var ANON_ID_STORAGE_KEY = 'cu-step-ab-anon-id';
  var DEBUG_VARIANT_STORAGE_KEY = 'cu-step-debug-ui-variant';
  var currentConfigSignature = '';

  function $(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function cloneConfig(source) {
    var copy = {};
    Object.keys(source || {}).forEach(function (key) {
      copy[key] = source[key];
    });
    return copy;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function normalizeUiVariant(value) {
    var normalized = String(value || '').toLowerCase().trim();
    return normalized === 'upsell2' || normalized === 'b' ? 'upsell2' : 'upsell1';
  }

  function isVariantEnabled(value) {
    var variantName = normalizeUiVariant(value);

    if (variantName === 'upsell2') {
      return !!(CONFIG.upsell2Enabled && CONFIG.upsell2ProductHandle);
    }

    return !!(CONFIG.upsell1Enabled && CONFIG.upsell1ProductHandle);
  }

  function getFallbackUiVariant(value) {
    var variantName = normalizeUiVariant(value);
    return variantName === 'upsell2' ? 'upsell1' : 'upsell2';
  }

  function resolveUsableUiVariant(value) {
    var variantName = normalizeUiVariant(value);
    if (isVariantEnabled(variantName)) return variantName;

    var fallback = getFallbackUiVariant(variantName);
    if (isVariantEnabled(fallback)) return fallback;

    return variantName;
  }

  function getProductHandleForVariant(value) {
    var variantName = normalizeUiVariant(value);
    return variantName === 'upsell2' ? CONFIG.upsell2ProductHandle : CONFIG.upsell1ProductHandle;
  }

  function getProductDataForVariant(value) {
    return productDataByVariant[normalizeUiVariant(value)];
  }

  function getActiveProductData() {
    return getProductDataForVariant(activeUiVariant);
  }

  function getStoredVariantId(value) {
    return selectedVariantIdByVariant[normalizeUiVariant(value)] || '';
  }

  function setStoredVariantId(value, variantId) {
    selectedVariantIdByVariant[normalizeUiVariant(value)] = variantId ? String(variantId) : '';
  }

  function hasConfiguredProducts(config) {
    if (!config || config.enabled === false) return false;
    if (config.forceUiVariant) {
      return normalizeUiVariant(config.uiVariant) === 'upsell2'
        ? !!(config.upsell2Enabled && config.upsell2ProductHandle)
        : !!(config.upsell1Enabled && config.upsell1ProductHandle);
    }
    return !!(
      (config.upsell1Enabled && config.upsell1ProductHandle) ||
      (config.upsell2Enabled && config.upsell2ProductHandle)
    );
  }

  function getProductHandleForConfig(config, value) {
    var variantName = normalizeUiVariant(value);
    if (variantName === 'upsell2') {
      return config && config.upsell2ProductHandle ? String(config.upsell2ProductHandle) : '';
    }
    return config && config.upsell1ProductHandle ? String(config.upsell1ProductHandle) : '';
  }

  function resetVariantCacheIfNeeded(previousConfig, nextConfig, value) {
    var variantName = normalizeUiVariant(value);
    var previousHandle = getProductHandleForConfig(previousConfig, variantName);
    var nextHandle = getProductHandleForConfig(nextConfig, variantName);

    if (previousHandle === nextHandle) return;

    productDataByVariant[variantName] = null;
    productRequestByVariant[variantName] = null;
    setStoredVariantId(variantName, '');
  }

  function getConfigSignature(config) {
    try {
      return JSON.stringify(config || {});
    } catch (_) {
      return 'unserializable';
    }
  }

  function setRuntimeConfig(nextConfig) {
    var resolvedConfig = cloneConfig(nextConfig || BASE_CONFIG);

    resetVariantCacheIfNeeded(CONFIG, resolvedConfig, 'upsell1');
    resetVariantCacheIfNeeded(CONFIG, resolvedConfig, 'upsell2');

    CONFIG = resolvedConfig;
    currentConfigSignature = getConfigSignature(resolvedConfig);
  }

  function getCheckoutScopeSelector() {
    var ctx = window.__cuStepCheckoutContext || {};
    if (ctx.formId === 'cart') return '#main-cart-items .cart-item';
    if (ctx.formId === 'CartDrawer-Form') return '#CartDrawer .cart-item';
    return '#CartDrawer .cart-item, #main-cart-items .cart-item';
  }

  function getEligibleCartLineItems() {
    var selector = getCheckoutScopeSelector();
    var nodes = Array.prototype.slice.call(document.querySelectorAll(selector));

    return nodes.filter(function (node) {
      return node && node.getAttribute('data-cu-source-eligible') !== 'false';
    });
  }

  function normalizeTemplateOfferConfig(source) {
    if (!source || source.enabled === false) return { enabled: false, disableGlobalFallback: true };
    var one = source.upsell1 || {};
    var two = source.upsell2 || {};
    var shared = source.shared || {};
    var selected = normalizeUiVariant(source.uiVariant) === 'upsell2' ? two : one;
    if (selected.enabled === false) return { enabled: false, disableGlobalFallback: true };
    var next = {
      enabled: true,
      disableGlobalFallback: source.fallbackBehavior === 'skip',
      forceUiVariant: true,
      uiVariant: normalizeUiVariant(source.uiVariant),
      upsell1Enabled: one.enabled !== false,
      upsell2Enabled: two.enabled !== false,
      upsell1ProductHandle: one.productHandle || '',
      upsell2ProductHandle: two.productHandle || '',
      upsell1VariantMoneyMap: {},
      upsell2VariantMoneyMap: {},
      originalPrice: shared.originalPrice || '',
      oncePerSession: shared.oncePerSession,
      timerSeconds: shared.timerSeconds
    };
    var maps = {
      upsell1: { heroText: 'HeroTemplate', ratingText: 'RatingText', timerLabel: 'TimerLabel', subtext: 'SubtextTemplate', reasonsText: 'ReasonsText', chooseColorText: 'ChooseVariantText', variantLabel: 'VariantLabel', titleTemplate: 'TitleTemplate', acceptText: 'AcceptText', declineText: 'DeclineText' },
      upsell2: { ratingText: 'RatingText', timerLabel: 'TimerLabel', titleTemplate: 'TitleTemplate', variantLabel: 'VariantLabel', benefitsHeading: 'BenefitsHeading', benefit1: 'Benefit1', benefit2: 'Benefit2', benefit3: 'Benefit3', acceptText: 'AcceptText', declineText: 'DeclineText', image: 'Image', badgeImage: 'BadgeImage', badgeAlt: 'BadgeAlt', saveText: 'SaveText', timerSeconds: 'TimerSeconds', socialText: 'SocialText', avatars: 'Avatars' }
    };
    ['upsell1', 'upsell2'].forEach(function (name) {
      var values = (source[name] || {}).settings || {};
      Object.keys(maps[name]).forEach(function (key) {
        if (values[key] != null) next[name + maps[name][key]] = values[key];
      });
    });
    return next;
  }

  function readProductOverrideConfig(node) {
    if (!node) return null;
    try {
      var templateConfig = node.getAttribute('data-cu-template-config');
      if (templateConfig) return normalizeTemplateOfferConfig(JSON.parse(templateConfig));
      var raw = node.getAttribute('data-cu-upsell-config');
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  }

  function readPageTemplateConfig() {
    var node = document.querySelector('script[data-template-upsell-config]');
    try {
      var source = node ? JSON.parse(node.textContent) : BASE_CONFIG.pageTemplateOfferConfig;
      if (!source) return null;
      var config = normalizeTemplateOfferConfig(source);
      if (hasConfiguredProducts(config)) return config;
      return config.disableGlobalFallback ? { enabled: false, disableGlobalFallback: true } : null;
    } catch (_) {
      return null;
    }
  }

  function resolveProductOverrideConfig() {
    var pageConfig = readPageTemplateConfig();
    if (window.Shopify && window.Shopify.designMode && pageConfig) return pageConfig;
    var lines = getEligibleCartLineItems();

    for (var i = 0; i < lines.length; i += 1) {
      var parsed = readProductOverrideConfig(lines[i]);
      if (!parsed || typeof parsed !== 'object') continue;

      if (hasConfiguredProducts(parsed)) {
        return parsed;
      }

      if (parsed.disableGlobalFallback) {
        return {
          enabled: false
        };
      }
    }

    return pageConfig;
  }

  function mergeOfferConfig(override) {
    var base = cloneConfig(BASE_CONFIG);

    if (!override) return base;
    if (override.enabled === false) {
      base.enabled = false;
      return base;
    }

    Object.keys(override).forEach(function (key) {
      base[key] = override[key];
    });

    return base;
  }

  function resolveEffectiveConfig() {
    return activeAddConfig ? cloneConfig(activeAddConfig) : mergeOfferConfig(resolveProductOverrideConfig());
  }

  function resolveAddedProductConfig(formData, response) {
    var properties = response && response.properties || {};
    var nested = formData.get('properties[_cu_step_config]') || properties._cu_step_config;
    var flat = formData.get('properties[_cu_upsell_config]') || properties._cu_upsell_config;
    var override = null;
    try {
      if (nested) override = normalizeTemplateOfferConfig(JSON.parse(nested));
      else if (flat) override = JSON.parse(flat);
      else {
        var page = document.querySelector('script[data-template-upsell-config]');
        if (page && response && String(response.product_id) === page.dataset.productId) {
          override = readPageTemplateConfig();
        }
      }
    } catch (_) {}

    if (override && !hasConfiguredProducts(override)) {
      override = override.disableGlobalFallback ? { enabled: false } : null;
    }
    return mergeOfferConfig(override);
  }

  function applyResolvedConfig() {
    var nextConfig = resolveEffectiveConfig();
    var nextSignature = getConfigSignature(nextConfig);

    if (nextSignature === currentConfigSignature) return;
    setRuntimeConfig(nextConfig);
  }

  function generateAnonId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }

    return 'anon-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  function getAnonId() {
    try {
      var existing = localStorage.getItem(ANON_ID_STORAGE_KEY);
      if (existing) return existing;

      var created = generateAnonId();
      localStorage.setItem(ANON_ID_STORAGE_KEY, created);
      return created;
    } catch (_) {
      return '';
    }
  }

  function simpleHash(str) {
    var input = String(str || '');
    var h = 0;

    for (var i = 0; i < input.length; i += 1) {
      h = ((h << 5) - h) + input.charCodeAt(i);
      h |= 0;
    }

    return h >>> 0;
  }

  function getAssignedUiVariant() {
    var experimentName = asTrimmedText(CONFIG.experimentName) || 'cart-upsell-step-ui-v1';
    var anonId = getAnonId();
    if (!anonId) {
      return resolveUsableUiVariant(CONFIG.uiVariant || 'upsell1');
    }

    var bucket = simpleHash(experimentName + ':' + anonId) % 100;
    return resolveUsableUiVariant(bucket < 50 ? 'upsell1' : 'upsell2');
  }

  function readUiVariantFromUrl() {
    try {
      var raw = new URLSearchParams(window.location.search).get('cu_step_variant');
      if (!raw) return '';
      return normalizeUiVariant(String(raw).toLowerCase());
    } catch (_) {
      return '';
    }
  }

  function readDebugUiVariantFromStorage() {
    try {
      var raw = localStorage.getItem(DEBUG_VARIANT_STORAGE_KEY);
      if (!raw) return '';
      return normalizeUiVariant(String(raw).toLowerCase());
    } catch (_) {
      return '';
    }
  }

  function getPreferredUiVariant() {
    var fromUrl = readUiVariantFromUrl();
    if (fromUrl) return resolveUsableUiVariant(fromUrl);

    if (shouldShowDevToggle()) {
      var fromStorage = readDebugUiVariantFromStorage();
      if (fromStorage) return resolveUsableUiVariant(fromStorage);
    }

    if (CONFIG.forceUiVariant) return normalizeUiVariant(CONFIG.uiVariant);
    return getAssignedUiVariant();
  }

  function persistDebugUiVariant(value) {
    try {
      localStorage.setItem(DEBUG_VARIANT_STORAGE_KEY, normalizeUiVariant(value));
    } catch (_) {}
  }

  function shouldShowDevToggle() {
    return !!CONFIG.debugSwitcherEnabled;
  }

  function refreshDevToggleUi() {
    var toggle = $('#cu-step-dev-toggle');
    if (!toggle) return;

    toggle.hidden = !shouldShowDevToggle();

    var buttons = toggle.querySelectorAll('[data-cu-variant-choice]');
    buttons.forEach(function (btn) {
      var v = normalizeUiVariant(btn.getAttribute('data-cu-variant-choice') || 'upsell1');
      btn.disabled = !isVariantEnabled(v);
      btn.classList.toggle('is-active', v === activeUiVariant);
    });
  }

  function getMoneyFormat() {
    var configured = asTrimmedText(CONFIG.moneyFormat);
    return configured || '${{amount}}';
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    var decimals = precision == null ? 2 : precision;
    var thousandsSeparator = thousands == null ? ',' : thousands;
    var decimalSeparator = decimal == null ? '.' : decimal;
    var value = Number(number || 0);

    if (!Number.isFinite(value)) value = 0;

    var fixed = value.toFixed(decimals);
    var parts = fixed.split('.');
    var dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
    var cents = parts[1] ? decimalSeparator + parts[1] : '';
    return dollars + cents;
  }

  function formatMoney(cents) {
    if (cents == null || cents === '') return '';

    var centsValue = Number(cents);
    if (!Number.isFinite(centsValue)) return '';

    var format = getMoneyFormat();
    var formatString = /\{\{\s*(\w+)\s*\}\}/;
    var placeholderMatch = format.match(formatString);
    var placeholder = placeholderMatch ? placeholderMatch[1] : 'amount';
    var amount = '';

    switch (placeholder) {
      case 'amount':
        amount = formatWithDelimiters(centsValue / 100, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        amount = formatWithDelimiters(centsValue / 100, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        amount = formatWithDelimiters(centsValue / 100, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = formatWithDelimiters(centsValue / 100, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        amount = formatWithDelimiters(centsValue / 100, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        amount = formatWithDelimiters(centsValue / 100, 0, ' ', '.');
        break;
      case 'amount_with_space_separator':
        amount = formatWithDelimiters(centsValue / 100, 2, ' ', '.');
        break;
      default:
        amount = formatWithDelimiters(centsValue / 100, 2, ',', '.');
    }

    return format.replace(formatString, amount);
  }

  function asTrimmedText(value) {
    if (value == null) return '';
    return String(value).trim();
  }

  function getSessionKey() {
    return (CONFIG && CONFIG.sessionKey) ? CONFIG.sessionKey : 'cu-step-shown-v2';
  }

  function markUpsellHandledForSession() {
    if (!CONFIG || !CONFIG.oncePerSession) return;
    try {
      sessionStorage.setItem(getSessionKey(), '1');
    } catch (_) {}
  }

  function formatTimerValue(totalSeconds) {
    var safe = Math.max(0, totalSeconds | 0);
    var mins = Math.floor(safe / 60);
    var secs = safe % 60;
    return String(mins).padStart(2, '0') + ':' + String(secs).padStart(2, '0');
  }

  function stopOfferTimer() {
    if (offerTimerInterval) {
      clearInterval(offerTimerInterval);
      offerTimerInterval = null;
    }
  }

  function startOfferTimer() {
    stopOfferTimer();
    if (!refs.timerEl) return;

    var duration = activeUiVariant === 'upsell2' && CONFIG.upsell2TimerSeconds != null ? CONFIG.upsell2TimerSeconds : CONFIG.timerSeconds;
    var total = parseInt(duration, 10);
    var countdown = $('#cu2-countdown');
    if (activeUiVariant === 'upsell2' && countdown) countdown.hidden = !(total > 0);
    if (!total || total < 0) {
      refs.timerEl.textContent = '00:00';
      return;
    }

    var remaining = total;
    refs.timerEl.textContent = formatTimerValue(remaining);

    offerTimerInterval = setInterval(function () {
      remaining -= 1;
      if (remaining <= 0) {
        refs.timerEl.textContent = '00:00';
        stopOfferTimer();
        return;
      }
      refs.timerEl.textContent = formatTimerValue(remaining);
    }, 1000);
  }

  function stopVariantEnhancements() {
    stopOfferTimer();
  }

  function startVariantEnhancements() {
    startOfferTimer();
  }

  function resolveRefs() {
    refs = {
      root: $('#cu-step-root'),
      step: activeUiVariant === 'upsell2' ? $('#cu-step-v2') : $('#cu-step'),
      imgEl: activeUiVariant === 'upsell2' ? $('#cu2-img') : $('#cu-img'),
      titleEl: activeUiVariant === 'upsell2' ? $('#cu2-title') : $('#cu-title'),
      ratingEl: activeUiVariant === 'upsell2' ? $('#cu2-rating-text') : $('#cu-rating-text'),
      priceOrigLabel: activeUiVariant === 'upsell2' ? null : $('#cu-price-orig-label'),
      priceOrigEl: activeUiVariant === 'upsell2' ? $('#cu2-price-orig') : $('#cu-price-orig'),
      priceCurEl: activeUiVariant === 'upsell2' ? $('#cu2-price-cur') : $('#cu-price-cur'),
      priceSeparator: activeUiVariant === 'upsell2' ? null : $('#cu-price-separator'),
      saveAmountEl: activeUiVariant === 'upsell2' ? $('#cu2-save-amount') : null,
      variantWrap: activeUiVariant === 'upsell2' ? $('#cu2-variant-wrap') : $('#cu-variant-wrap'),
      variantLabel: activeUiVariant === 'upsell2' ? $('#cu2-variant-label') : $('#cu-variant-label'),
      variantSelect: activeUiVariant === 'upsell2' ? $('#cu2-variant-select') : $('#cu-variant-select'),
      optionSelectsWrap: activeUiVariant === 'upsell2' ? $('#cu2-option-selects') : null,
      variantButtonsWrap: null,
      acceptBtn: activeUiVariant === 'upsell2' ? $('#cu2-accept-btn') : $('#cu-accept-btn'),
      declineBtn: activeUiVariant === 'upsell2' ? $('#cu2-decline-btn') : $('#cu-decline-btn'),
      closeBtn: activeUiVariant === 'upsell2' ? $('#cu2-close-btn') : $('#cu-close-btn'),
      timerEl: activeUiVariant === 'upsell2' ? $('#cu2-timer') : $('#cu-timer'),
      errorEl: activeUiVariant === 'upsell2' ? $('#cu2-error') : null
    };
  }

  function setActiveUiVariant(nextVariant) {
    var resolved = resolveUsableUiVariant(nextVariant);

    var wasOpen = !!(refs.root && refs.root.classList.contains('cu-root-visible'));

    activeUiVariant = resolved;

    var root = $('#cu-step-root');
    if (root) {
      root.setAttribute('data-cu-variant', resolved);
    }

    var upsell1Panel = $('#cu-step');
    var upsell2Panel = $('#cu-step-v2');

    if (upsell1Panel) {
      upsell1Panel.classList.toggle('cu-step-panel-active', resolved === 'upsell1');
      upsell1Panel.classList.remove('cu-visible');
    }

    if (upsell2Panel) {
      upsell2Panel.classList.toggle('cu-step-panel-active', resolved === 'upsell2');
      upsell2Panel.classList.remove('cu-visible');
    }

    stopVariantEnhancements();
    resolveRefs();
    refreshDevToggleUi();
    updatePopupBadge();

    var activeProduct = getActiveProductData();
    if (activeProduct) {
      populatePanel(activeProduct, resolved);
    } else {
      preloadVariantProduct(resolved);
    }

    var selectedVariantId = getStoredVariantId(resolved);
    if (selectedVariantId) {
      applyVariantState(selectedVariantId, resolved);
    }

    if (wasOpen && refs.step) {
      refs.step.classList.add('cu-visible');
      startVariantEnhancements();
    }
  }

  function updatePopupBadge() {
    var badge = $('#cu2-badge');
    if (!badge) return;
    var image = asTrimmedText(CONFIG.upsell2BadgeImage);
    badge.hidden = !image;
    badge.alt = asTrimmedText(CONFIG.upsell2BadgeAlt);
    if (image) badge.src = image;
    else badge.removeAttribute('src');
  }

  function getConfiguredTitleText(productTitle, uiVariant) {
    var resolved = normalizeUiVariant(uiVariant);
    var upsell1Template = asTrimmedText(CONFIG.upsell1TitleTemplate) || asTrimmedText(CONFIG.upsell1TitleText) || '[product_title]';
    var upsell2Template = asTrimmedText(CONFIG.upsell2TitleTemplate) || asTrimmedText(CONFIG.upsell2TitleText) || upsell1Template;
    var template = resolved === 'upsell2' ? upsell2Template : upsell1Template;

    return renderTemplate(template, {
      '[product_title]': productTitle || ''
    });
  }

  function updateTitleText(productTitle, uiVariant) {
    if (!refs.titleEl) return;
    refs.titleEl.textContent = getConfiguredTitleText(productTitle, uiVariant);
  }

  function updateRatingText(uiVariant) {
    if (!refs.ratingEl) return;

    var upsell1Text = asTrimmedText(CONFIG.upsell1RatingText) || asTrimmedText(CONFIG.ratingText);
    var upsell2Text = asTrimmedText(CONFIG.upsell2RatingText) || upsell1Text;
    var text = normalizeUiVariant(uiVariant) === 'upsell2' ? upsell2Text : upsell1Text;
    if (text) refs.ratingEl.textContent = text;
  }

  function updateVariantLabels(product, uiVariant) {
    if (!refs.variantLabel) return;

    var fallbackLabel = (product && product.options && product.options[0] && product.options[0].name) ? product.options[0].name : '';
    var upsell1LabelText = asTrimmedText(CONFIG.upsell1VariantLabel) || fallbackLabel;
    var upsell2LabelText = asTrimmedText(CONFIG.upsell2VariantLabel) || fallbackLabel;
    refs.variantLabel.textContent = normalizeUiVariant(uiVariant) === 'upsell2' ? upsell2LabelText : upsell1LabelText;
  }

  function renderTemplate(template, replacements) {
    var output = String(template || '');
    Object.keys(replacements || {}).forEach(function (key) {
      output = output.split(key).join(replacements[key]);
    });
    return output;
  }

  function getVariantPriceText(variant, uiVariant) {
    if (!variant) return '';
    return getFormattedVariantMoney(variant, 'price', uiVariant) || formatMoney(variant.price);
  }

  function getPopup1HeroHtml(product, variant, uiVariant) {
    var template = asTrimmedText(CONFIG.upsell1HeroTemplate) || 'How about another [product_title] for [discount_label]?';
    var productTitle = escapeHtml(product && product.title ? product.title : '');
    var priceText = getVariantPriceText(variant, uiVariant);
    var emphasizedPrice = priceText ? '<strong>' + priceText + '</strong>' : '';

    return renderTemplate(template, {
      '[product_title]': productTitle,
      '[discount_label]': emphasizedPrice,
      '60% OFF': emphasizedPrice
    });
  }

  function getPopup1SubtextHtml(variant, uiVariant) {
    var template = asTrimmedText(CONFIG.upsell1SubtextTemplate) || 'Limited-time deal — add this to your order now and save big.';
    var priceText = getVariantPriceText(variant, uiVariant);
    var output = renderTemplate(template, {
      '[discount_label]': priceText,
      '60% OFF': priceText
    });

    if (priceText) {
      output = output.split(priceText).join('<strong>' + priceText + '</strong>');
    }

    var chooseVariantText = asTrimmedText(CONFIG.upsell1ChooseVariantText);
    if (chooseVariantText) {
      output += ' <strong>' + escapeHtml(chooseVariantText) + '</strong>';
    }

    return output;
  }

  function updatePanelCopyForVariant(variant, uiVariant) {
    var resolved = normalizeUiVariant(uiVariant || activeUiVariant);
    var product = getProductDataForVariant(resolved);

    if (!product || !variant) return;

    if (resolved === 'upsell2') {
      var timerCaption = $('#cu2-timer-caption');
      if (timerCaption) timerCaption.textContent = asTrimmedText(CONFIG.upsell2TimerLabel);
      var description = $('#cu2-description');
      description.textContent = asTrimmedText(CONFIG.upsell2BenefitsHeading);
      description.hidden = !description.textContent;
      [1, 2, 3].forEach(function (index) {
        var node = $('#cu2-benefit-' + index + '-copy');
        node.textContent = asTrimmedText(CONFIG['upsell2Benefit' + index]);
        node.parentElement.hidden = !node.textContent;
      });
      refs.acceptBtn.textContent = asTrimmedText(CONFIG.upsell2AcceptText) || 'Add to order with discount';
      refs.declineBtn.textContent = asTrimmedText(CONFIG.upsell2DeclineText) || 'No thanks, continue to check-out';
      var avatars = $('#cu2-avatars');
      avatars.innerHTML = (CONFIG.upsell2Avatars || []).filter(Boolean).map(function (url) {
        return '<img src="' + escapeHtml(url) + '" width="24" height="24" alt="">';
      }).join('');
      $('#cu2-social-text').textContent = asTrimmedText(CONFIG.upsell2SocialText);
      $('#cu2-social').hidden = !avatars.children.length && !asTrimmedText(CONFIG.upsell2SocialText);
      return;
    }

    setNodeContent($('#cu-hero-title'), getPopup1HeroHtml(product, variant, resolved));
    setNodeContent($('#cu-subtext'), getPopup1SubtextHtml(variant, resolved));

    var popup1Reasons = $('#cu-reasons-text');
    if (popup1Reasons) {
      var reasonsText = asTrimmedText(CONFIG.upsell1ReasonsText);
      setNodeContent(popup1Reasons, reasonsText.replace(/\n/g, '<br>'));
      popup1Reasons.style.display = reasonsText ? '' : 'none';
    }

    var popup1TimerLabel = $('#cu-timer-label');
    if (popup1TimerLabel) popup1TimerLabel.textContent = asTrimmedText(CONFIG.upsell1TimerLabel) || 'Offer expires in:';

    if (refs.acceptBtn) refs.acceptBtn.textContent = asTrimmedText(CONFIG.upsell1AcceptText) || 'Add to order';
    if (refs.declineBtn) refs.declineBtn.textContent = asTrimmedText(CONFIG.upsell1DeclineText) || 'Decline this offer';
  }

  function updateImageForVariant(variant, uiVariant) {
    var product = getProductDataForVariant(uiVariant);
    var variantImage = variant && variant.featured_image && variant.featured_image.src ? variant.featured_image.src : null;
    var fallback = product && product.images && product.images[0] ? product.images[0] : '';
    var customImage = normalizeUiVariant(uiVariant) === 'upsell2' ? asTrimmedText(CONFIG.upsell2Image) : '';
    var newSrc = customImage || variantImage || fallback;
    var imgEl = refs.imgEl;
    if (!imgEl) return;
    imgEl.hidden = !newSrc;
    if (!newSrc) return;
    if (imgEl.src === newSrc && imgEl.alt === (product && product.title ? product.title : '')) return;

    imgEl.style.opacity = '0';
    imgEl.src = newSrc;
    imgEl.alt = product && product.title ? product.title : '';

    var fadeIn = function () {
      imgEl.style.opacity = '1';
    };

    if (imgEl.complete) {
      requestAnimationFrame(fadeIn);
    } else {
      imgEl.onload = fadeIn;
    }
  }

  function getVariantMoneyMap(uiVariant) {
    var resolved = normalizeUiVariant(uiVariant || activeUiVariant);
    if (resolved === 'upsell2') return CONFIG.upsell2VariantMoneyMap || {};
    return CONFIG.upsell1VariantMoneyMap || {};
  }

  function getFormattedVariantMoney(variant, key, uiVariant) {
    if (!variant || !key) return '';
    var variantMoneyMap = getVariantMoneyMap(uiVariant);
    if (!variantMoneyMap) return '';

    var entry = variantMoneyMap[String(variant.id)];
    if (!entry || typeof entry !== 'object') return '';

    return asTrimmedText(entry[key]);
  }

  function containsHtml(value) {
    return /<[^>]+>/.test(String(value || ''));
  }

  function setNodeContent(node, value) {
    if (!node) return;

    var content = value == null ? '' : String(value);
    if (containsHtml(content)) {
      node.innerHTML = content;
    } else {
      node.textContent = content;
    }
  }

  function refreshConvertedMoney() {
    if (!window.bucksCC || typeof window.bucksCC.reConvert !== 'function') return;

    try {
      window.bucksCC.reConvert();
    } catch (_) {}
  }

  function updatePriceRowForVariant(variant) {
    if (!variant) return;

    updatePanelCopyForVariant(variant, activeUiVariant);

    if (refs.priceCurEl) {
      var basePriceText = getFormattedVariantMoney(variant, 'price') || formatMoney(variant.price);
      setNodeContent(refs.priceCurEl, basePriceText);
    }

    var configuredOriginalPrice = asTrimmedText(CONFIG.originalPrice);
    var hasOriginalPrice = false;
    var originalPriceText = '';

    if (configuredOriginalPrice) {
      hasOriginalPrice = true;
      originalPriceText = configuredOriginalPrice;
    } else if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      hasOriginalPrice = true;
      originalPriceText = getFormattedVariantMoney(variant, 'compareAt') || formatMoney(variant.compare_at_price);
    }

    if (refs.priceOrigEl) {
      if (hasOriginalPrice) {
        setNodeContent(refs.priceOrigEl, originalPriceText);
        refs.priceOrigEl.style.display = '';
      } else {
        refs.priceOrigEl.style.display = 'none';
      }
    }

    if (refs.priceOrigLabel) {
      refs.priceOrigLabel.style.display = hasOriginalPrice ? '' : 'none';
    }

    if (refs.priceSeparator) {
      refs.priceSeparator.style.display = hasOriginalPrice ? '' : 'none';
    }

    var saveText = '';
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      var formattedSaveAmount = getFormattedVariantMoney(variant, 'save') || formatMoney(variant.compare_at_price - variant.price);
      saveText = 'You save ' + formattedSaveAmount;
    }

    if (refs.saveAmountEl) {
      setNodeContent(refs.saveAmountEl, saveText);
      refs.saveAmountEl.style.display = saveText ? '' : 'none';
    }

    var saveBadge = activeUiVariant === 'upsell2' ? $('#cu2-save-badge') : null;
    if (saveBadge) {
      if (saveText) {
        setNodeContent(saveBadge, escapeHtml(asTrimmedText(CONFIG.upsell2SaveText) || 'Save [amount]!').replace('[amount]', getFormattedVariantMoney(variant, 'save') || formatMoney(variant.compare_at_price - variant.price)));
        saveBadge.style.display = '';
      } else {
        saveBadge.style.display = 'none';
      }
    }

    refreshConvertedMoney();
  }

  function getVariantById(variantId, uiVariant) {
    var product = getProductDataForVariant(uiVariant || activeUiVariant);
    if (!product || !product.variants || !variantId) return null;
    var wanted = String(variantId);

    for (var i = 0; i < product.variants.length; i += 1) {
      if (String(product.variants[i].id) === wanted) return product.variants[i];
    }

    return null;
  }

  function getVariantOptionValue(variant, index) {
    if (!variant) return '';
    var value = Array.isArray(variant.options) ? variant.options[index] : variant['option' + (index + 1)];
    return value == null ? '' : String(value);
  }

  function getSelectedOptionVariant(product) {
    if (!product || !product.variants || !refs.optionSelectsWrap || refs.optionSelectsWrap.hidden) return null;

    var selects = Array.prototype.slice.call(refs.optionSelectsWrap.querySelectorAll('select[data-cu-option-index]'));
    if (!selects.length) return null;

    return product.variants.find(function (variant) {
      return selects.every(function (select) {
        return getVariantOptionValue(variant, Number(select.getAttribute('data-cu-option-index'))) === select.value;
      });
    }) || null;
  }

  function handleOptionSelectionChange() {
    if (activeUiVariant !== 'upsell2') return;

    var variant = getSelectedOptionVariant(getActiveProductData());
    if (!variant) {
      setStoredVariantId('upsell2', '');
      if (refs.acceptBtn) refs.acceptBtn.disabled = true;
    } else {
      applyVariantState(variant.id, 'upsell2');
    }

    if (refs.errorEl) {
      refs.errorEl.textContent = !variant ? 'This combination is unavailable.' : variant.available ? '' : 'This combination is sold out.';
      refs.errorEl.hidden = !!(variant && variant.available);
    }
  }

  function syncVariantControls(variant) {
    if (!variant) return;

    var wantedId = String(variant.id);

    if (refs.variantSelect) {
      refs.variantSelect.value = wantedId;
    }

    if (refs.optionSelectsWrap && !refs.optionSelectsWrap.hidden) {
      refs.optionSelectsWrap.querySelectorAll('select[data-cu-option-index]').forEach(function (select) {
        select.value = getVariantOptionValue(variant, Number(select.getAttribute('data-cu-option-index')));
      });
    }

    if (refs.variantButtonsWrap) {
      var buttons = refs.variantButtonsWrap.querySelectorAll('[data-cu-variant-id]');
      buttons.forEach(function (btn) {
        btn.classList.toggle('active', btn.getAttribute('data-cu-variant-id') === wantedId);
      });
    }

    if (refs.acceptBtn && !refs.acceptBtn.classList.contains('cu-loading')) {
      refs.acceptBtn.disabled = !variant.available;
    }
  }

  function applyVariantState(variantId, uiVariant) {
    var resolved = normalizeUiVariant(uiVariant || activeUiVariant);
    var variant = getVariantById(variantId, resolved);
    if (!variant) return;

    setStoredVariantId(resolved, variant.id);

    if (resolved !== activeUiVariant) {
      return;
    }

    updatePriceRowForVariant(variant);
    updateImageForVariant(variant, resolved);
    syncVariantControls(variant);
  }

  function buildVariantControls(product, uiVariant) {
    if (!product || normalizeUiVariant(uiVariant) !== activeUiVariant) return;

    var variants = product && product.variants ? product.variants : [];
    if (!variants.length) return;

    var firstAvailableId = '';
    variants.forEach(function (variant) {
      if (!firstAvailableId && variant.available) firstAvailableId = String(variant.id);
    });

    var optionDefinitions = Array.isArray(product.options) ? product.options.map(function (option, index) {
      var name = typeof option === 'string' ? option : option && option.name;
      var values = option && Array.isArray(option.values) ? option.values : [];
      if (!values.length) {
        values = variants.map(function (variant) { return getVariantOptionValue(variant, index); });
      }
      return {
        name: name || 'Option ' + (index + 1),
        values: values.map(String).filter(function (value, valueIndex, allValues) {
          return value && allValues.indexOf(value) === valueIndex;
        })
      };
    }) : [];
    var useOptionSelects = activeUiVariant === 'upsell2' && optionDefinitions.length > 1 && optionDefinitions.every(function (option) {
      return option.values.length > 0;
    });

    if (refs.optionSelectsWrap) {
      refs.optionSelectsWrap.innerHTML = '';
      refs.optionSelectsWrap.hidden = !useOptionSelects;
      var hint = $('#cu2-variant-hint');
      var hintText = asTrimmedText(CONFIG.upsell2VariantLabel);
      if (hint) {
        hint.textContent = hintText;
        hint.hidden = !useOptionSelects || !hintText || optionDefinitions.some(function (option) {
          return option.name.toLowerCase() === hintText.toLowerCase();
        });
      }
      if (useOptionSelects) {
        optionDefinitions.forEach(function (option, index) {
          var field = document.createElement('div');
          field.className = 'cu2-option-field';
          var label = document.createElement('label');
          label.htmlFor = 'cu2-option-' + index;
          label.textContent = option.name;
          var select = document.createElement('select');
          select.id = label.htmlFor;
          select.setAttribute('data-cu-option-index', String(index));
          option.values.forEach(function (value) {
            var choice = document.createElement('option');
            choice.value = value;
            choice.textContent = value;
            select.appendChild(choice);
          });
          field.appendChild(label);
          field.appendChild(select);
          refs.optionSelectsWrap.appendChild(field);
        });
      }
    }

    if (refs.variantSelect) {
      refs.variantSelect.innerHTML = '';
      refs.variantSelect.hidden = useOptionSelects;
      if (!useOptionSelects) {
        variants.forEach(function (variant) {
          var opt = document.createElement('option');
          opt.value = String(variant.id);
          opt.textContent = variant.title;
          opt.disabled = !variant.available;
          refs.variantSelect.appendChild(opt);
        });
      }
    }
    if (activeUiVariant === 'upsell2' && refs.variantLabel) refs.variantLabel.hidden = useOptionSelects;

    if (refs.variantButtonsWrap) {
      refs.variantButtonsWrap.innerHTML = '';
      variants.forEach(function (variant) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cu2-v-btn';
        btn.textContent = variant.title;
        btn.setAttribute('data-cu-variant-id', String(variant.id));
        btn.disabled = !variant.available;
        btn.addEventListener('click', function () {
          applyVariantState(String(variant.id), uiVariant);
        });
        refs.variantButtonsWrap.appendChild(btn);
      });
    }

    var hasMultiple = variants.length > 1;
    if (activeUiVariant === 'upsell1' && refs.variantWrap) {
      refs.variantWrap.style.display = hasMultiple ? '' : 'none';
    }
    if (activeUiVariant === 'upsell2' && refs.variantWrap) {
      refs.variantWrap.hidden = !hasMultiple;
    }

    updateVariantLabels(product, uiVariant);

    if (!firstAvailableId) {
      firstAvailableId = String(variants[0].id);
    }

    var preferredVariantId = getStoredVariantId(uiVariant);
    if (!getVariantById(preferredVariantId, uiVariant)) {
      preferredVariantId = firstAvailableId;
    }

    applyVariantState(preferredVariantId, uiVariant);
  }

  function fetchProduct(handle) {
    if (!handle) return Promise.resolve(null);

    return fetch(((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'products/' + encodeURIComponent(handle) + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function preloadProductImages(product) {
    if (!product || !product.variants) return;

    product.variants.forEach(function (variant) {
      var imgSrc = variant.featured_image && variant.featured_image.src ? variant.featured_image.src : null;
      if (!imgSrc) return;
      var preload = new Image();
      preload.src = imgSrc;
    });
  }

  function preloadVariantProduct(uiVariant) {
    if (!CONFIG.enabled) return Promise.resolve(null);
    var resolved = normalizeUiVariant(uiVariant);
    var handle = getProductHandleForVariant(resolved);
    if (!handle) return Promise.resolve(null);

    if (productDataByVariant[resolved]) {
      return Promise.resolve(productDataByVariant[resolved]);
    }

    if (productRequestByVariant[resolved]) {
      return productRequestByVariant[resolved];
    }

    productRequestByVariant[resolved] = fetchProduct(handle)
      .then(function (data) {
        if (handle !== getProductHandleForVariant(resolved)) return null;
        productDataByVariant[resolved] = data;
        preloadProductImages(data);

        if (resolved === activeUiVariant) {
          if (data) populatePanel(data, resolved);
          else if (refs.errorEl) {
            refs.errorEl.textContent = 'This offer could not be loaded. You can continue to checkout below.';
            refs.errorEl.hidden = false;
          }
        }

        productRequestByVariant[resolved] = null;
        return data;
      })
      .catch(function () {
        if (handle === getProductHandleForVariant(resolved)) productRequestByVariant[resolved] = null;
        return null;
      });

    return productRequestByVariant[resolved];
  }

  function populatePanel(product, uiVariant) {
    if (!product) return;
    if (normalizeUiVariant(uiVariant) !== activeUiVariant) return;

    updateTitleText(product.title, uiVariant);
    updateRatingText(uiVariant);

    // Upsell 2 updates its image through applyVariantState so the previous image stays hidden until the replacement loads.
    if (activeUiVariant !== 'upsell2' && refs.imgEl && product.images && product.images[0]) {
      refs.imgEl.src = product.images[0];
      refs.imgEl.alt = product.title;
    }

    buildVariantControls(product, uiVariant);
    fitCenteredOffer();
  }

  function addVariantToCart(variantId) {
    var config = (typeof fetchConfig === 'function') ? fetchConfig('javascript') : {
      method: 'POST',
      headers: { Accept: 'application/javascript' }
    };

    config.headers = config.headers || {};
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    var formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', '1');

    var isCenteredOffer = activeUiVariant === 'upsell2';
    if (isCenteredOffer) formData.append('properties[__addon_upsell]', 'true');
    var drawer = isCenteredOffer ? null : $('cart-drawer');
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      formData.append('sections', drawer.getSectionsToRender().map(function (section) { return section.id; }));
      formData.append('sections_url', window.location.pathname);
    }

    config.body = formData;

    var cartAddUrl = (window.routes && window.routes.cart_add_url) ? window.routes.cart_add_url : '/cart/add';

    return fetch(cartAddUrl, config)
      .then(function (r) {
        return r.json().catch(function () { return null; });
      })
      .then(function (payload) {
        if (!payload) throw new Error('Unable to add selected variant');
        if (payload.status) throw new Error(payload.description || 'Unable to add selected variant');
        return payload;
      })
      .then(function (payload) {
        if (isCenteredOffer) return payload;
        if (drawer && payload.sections && typeof drawer.renderContents === 'function') {
          drawer.renderContents(payload);
          return payload;
        }
        return refreshCartDrawerUI().then(function () { return payload; });
      })
      .catch(function (err) {
        if (isCenteredOffer) throw err;
        return refreshCartDrawerUI()
          .then(function () { throw err; })
          .catch(function () { throw err; });
      });
  }

  function setAcceptLoading(isLoading) {
    ['upsell1', 'upsell2'].forEach(function (uiVariant) {
      var btn = $(uiVariant === 'upsell2' ? '#cu2-accept-btn' : '#cu-accept-btn');
      if (!btn) return;

      if (isLoading) {
        btn.disabled = true;
        btn.classList.add('cu-loading');
        return;
      }

      btn.classList.remove('cu-loading');

      var current = getVariantById(getStoredVariantId(uiVariant), uiVariant);
      btn.disabled = uiVariant === 'upsell2' ? !current || !current.available : !!(current && !current.available);
    });
  }

  function fitCenteredOffer() {
    var panel = $('#cu-step-v2');
    var root = $('#cu-step-root');
    if (activeUiVariant !== 'upsell2' || !panel || !root || !panel.offsetHeight) return;

    // Keep the offer above Shopify's fixed preview toolbar when it is present.
    var bottomInset = 0;
    document.querySelectorAll('#PBarNextFrameWrapper, iframe[id*="preview-bar"], iframe[id*="admin-bar"]').forEach(function (bar) {
      var rect = bar.getBoundingClientRect();
      if (rect.width && rect.height && rect.top > 0 && rect.bottom >= window.innerHeight - 1) {
        bottomInset = Math.max(bottomInset, window.innerHeight - rect.top);
      }
    });
    var insetValue = bottomInset + 'px';
    if (root.style.getPropertyValue('--cu2-bottom-inset') !== insetValue) {
      root.style.setProperty('--cu2-bottom-inset', insetValue);
    }
  }

  function showPanel() {
    if (offerBusy) return true;
    applyResolvedConfig();
    if (!CONFIG || !CONFIG.enabled || !hasConfiguredProducts(CONFIG)) return false;

    setActiveUiVariant(getPreferredUiVariant());

    if (!refs.root || !refs.step) return false;

    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }

    setAcceptLoading(false);

    previousFocus = document.activeElement;
    var drawer = $('cart-drawer');
    drawerWasOpen = !!(drawer && drawer.classList.contains('active'));
    if (activeUiVariant === 'upsell2' && drawerWasOpen && typeof drawer.close === 'function') drawer.close();
    refs.root.setAttribute('aria-hidden', 'false');
    refs.root.scrollTop = 0;
    if (refs.errorEl) refs.errorEl.hidden = true;
    refs.root.classList.add('cu-root-visible');
    document.body.classList.add('cu-step-open');

    requestAnimationFrame(function () {
      fitCenteredOffer();
      if (refs.step) refs.step.classList.add('cu-visible');
      startVariantEnhancements();
      if (activeUiVariant === 'upsell2' && refs.step) refs.step.focus({ preventScroll: true });
    });

    return true;
  }

  function hidePanel(callback) {
    if (!refs.root) {
      stopVariantEnhancements();
      activeAddConfig = null;
      if (callback) callback();
      return;
    }

    var upsell1Panel = $('#cu-step');
    var upsell2Panel = $('#cu-step-v2');
    var isVisible = !!((upsell1Panel && upsell1Panel.classList.contains('cu-visible')) || (upsell2Panel && upsell2Panel.classList.contains('cu-visible')));

    if (!isVisible) {
      refs.root.setAttribute('aria-hidden', 'true');
      refs.root.classList.remove('cu-root-visible');
      document.body.classList.remove('cu-step-open');
      stopVariantEnhancements();
      activeAddConfig = null;
      if (callback) callback();
      return;
    }

    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }

    if (upsell1Panel) upsell1Panel.classList.remove('cu-visible');
    if (upsell2Panel) upsell2Panel.classList.remove('cu-visible');

    stopVariantEnhancements();

    hideTimer = setTimeout(function () {
      refs.root.setAttribute('aria-hidden', 'true');
      refs.root.classList.remove('cu-root-visible');
      document.body.classList.remove('cu-step-open');
      hideTimer = null;
      activeAddConfig = null;
      if (callback) callback();
    }, 340);
  }

  function refreshCartDrawerUI() {
    var drawer = $('cart-drawer');
    if (!drawer || typeof drawer.renderContents !== 'function') {
      return Promise.resolve();
    }

    var cartUrl = (window.routes && window.routes.cart_url) ? window.routes.cart_url : '/cart';
    var sectionsUrl = cartUrl + '?sections=cart-drawer,cart-icon-bubble';

    return fetch(sectionsUrl, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (sections) {
        if (!sections || !sections['cart-drawer'] || !sections['cart-icon-bubble']) return;
        drawer.renderContents({
          id: Date.now(),
          sections: sections
        });
      })
      .catch(function () {});
  }

  function resolveCheckoutTargets() {
    var ctx = window.__cuStepCheckoutContext || {};
    var cartForm = null;
    var checkoutBtn = null;

    if (ctx.formId) {
      cartForm = document.getElementById(ctx.formId);
    }

    if (!cartForm) {
      cartForm = $('#CartDrawer-Form') || $('#cart');
    }

    if (ctx.submitterId) {
      checkoutBtn = document.getElementById(ctx.submitterId);
    }

    if (!checkoutBtn && cartForm && ctx.submitterName) {
      checkoutBtn = cartForm.querySelector('[name="' + ctx.submitterName + '"]');
    }

    if (!checkoutBtn) {
      checkoutBtn = $('#CartDrawer-Checkout') || $('#checkout') || (cartForm && cartForm.querySelector('[name="checkout"]'));
    }

    return {
      cartForm: cartForm,
      checkoutBtn: checkoutBtn
    };
  }

  function proceedToCheckout() {
    var targets = resolveCheckoutTargets();
    var cartForm = targets.cartForm;
    var checkoutBtn = targets.checkoutBtn;

    window.__cuStepBypass = true;
    setTimeout(function () { window.__cuStepBypass = false; }, 3000);

    if (checkoutBtn && !cartForm && checkoutBtn.form) {
      cartForm = checkoutBtn.form;
    }

    if (!cartForm) {
      window.location.assign('/checkout');
      return;
    }

    if (typeof cartForm.requestSubmit === 'function') {
      if (checkoutBtn && checkoutBtn.form === cartForm) {
        cartForm.requestSubmit(checkoutBtn);
      } else {
        cartForm.requestSubmit();
      }
    } else {
      cartForm.submit();
    }

    window.__cuStepCheckoutContext = null;
  }

  function getSelectedVariantId() {
    if (activeUiVariant === 'upsell2' && refs.optionSelectsWrap && !refs.optionSelectsWrap.hidden) {
      var selectedOptionVariant = getSelectedOptionVariant(getActiveProductData());
      return selectedOptionVariant && selectedOptionVariant.available ? String(selectedOptionVariant.id) : '';
    }

    var storedId = getStoredVariantId(activeUiVariant);
    if (storedId) return storedId;

    if (refs.variantSelect && refs.variantSelect.value) {
      return String(refs.variantSelect.value);
    }

    var product = getActiveProductData();
    if (product && product.variants && product.variants[0]) {
      return String(product.variants[0].id);
    }

    return '';
  }

  function handleAccept() {
    if (offerBusy) return;
    var variantId = getSelectedVariantId();

    if (!variantId) {
      if (activeUiVariant === 'upsell2') return;
      markUpsellHandledForSession();
      hidePanel(function () {
        proceedToCheckout();
      });
      return;
    }

    var selected = getVariantById(variantId, activeUiVariant);
    if (activeUiVariant === 'upsell2' && (!selected || !selected.available)) return;
    offerBusy = true;
    if (refs.errorEl) refs.errorEl.hidden = true;
    setAcceptLoading(true);

    addVariantToCart(variantId)
      .then(function () {
        markUpsellHandledForSession();
        hidePanel(function () {
          proceedToCheckout();
          offerBusy = false;
        });
      })
      .catch(function (err) {
        if (err) {
          console.error('[Upsell Step] add-to-cart failed:', err.message || err);
        }
        offerBusy = false;
        if (refs.errorEl) {
          refs.errorEl.textContent = err && err.message ? err.message : 'Unable to add this item. Please try again.';
          refs.errorEl.hidden = false;
        }
        setAcceptLoading(false);
      });
  }

  function handleDecline() {
    if (offerBusy) return;
    markUpsellHandledForSession();
    if (activeUiVariant === 'upsell2') {
      handleClose();
      return;
    }
    hidePanel(function () {
      proceedToCheckout();
    });
  }

  function handleClose() {
    if (offerBusy) return;
    if (activeUiVariant === 'upsell2') {
      hidePanel(function () {
        var drawer = $('cart-drawer');
        if (drawerWasOpen && drawer && typeof drawer.open === 'function') drawer.open(previousFocus);
        else if (previousFocus && previousFocus.isConnected) previousFocus.focus({ preventScroll: true });
      });
      return;
    }
    markUpsellHandledForSession();
    hidePanel(function () {
      proceedToCheckout();
    });
  }

  function handleEscape(e) {
    if (e.key === 'Tab' && activeUiVariant === 'upsell2' && refs.root && refs.root.classList.contains('cu-root-visible')) {
      var controls = Array.prototype.slice.call(refs.step.querySelectorAll('button:not(:disabled), select:not(:disabled)')).filter(function (el) { return !el.closest('[hidden]') && el.getClientRects().length; });
      if (!controls.length) return;
      var first = controls[0];
      var last = controls[controls.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === refs.step || !refs.step.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (document.activeElement === last || !refs.step.contains(document.activeElement))) { e.preventDefault(); first.focus(); }
    }
    if (e.key !== 'Escape') return;

    var upsell1Visible = $('#cu-step') && $('#cu-step').classList.contains('cu-visible');
    var upsell2Visible = $('#cu-step-v2') && $('#cu-step-v2').classList.contains('cu-visible');
    if (!upsell1Visible && !upsell2Visible) return;

    handleClose();
  }

  function bindEvents() {
    if (hasBoundEvents) return;
    hasBoundEvents = true;

    ['#cu-accept-btn', '#cu2-accept-btn'].forEach(function (sel) {
      var btn = $(sel);
      if (btn) btn.addEventListener('click', handleAccept);
    });

    ['#cu-decline-btn', '#cu2-decline-btn'].forEach(function (sel) {
      var btn = $(sel);
      if (btn) btn.addEventListener('click', handleDecline);
    });

    ['#cu-close-btn', '#cu2-close-btn'].forEach(function (sel) {
      var btn = $(sel);
      if (btn) btn.addEventListener('click', handleClose);
    });

    ['#cu-variant-select', '#cu2-variant-select'].forEach(function (sel) {
      var selectEl = $(sel);
      if (!selectEl) return;
      selectEl.addEventListener('change', function () {
        var uiVariant = sel === '#cu2-variant-select' ? 'upsell2' : 'upsell1';
        applyVariantState(selectEl.value, uiVariant);
      });
    });

    var optionSelectsWrap = $('#cu2-option-selects');
    if (optionSelectsWrap) {
      optionSelectsWrap.addEventListener('change', function (event) {
        if (event.target.matches('select[data-cu-option-index]')) handleOptionSelectionChange();
      });
    }

    var toggle = $('#cu-step-dev-toggle');
    if (toggle) {
      toggle.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-cu-variant-choice]');
        if (!btn) return;

        var chosen = normalizeUiVariant(btn.getAttribute('data-cu-variant-choice') || 'upsell1');
        if (!isVariantEnabled(chosen)) return;
        persistDebugUiVariant(chosen);
        setActiveUiVariant(chosen);
      });
    }

    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', fitCenteredOffer);
    var previewBar = $('#PBarNextFrameWrapper');
    if (typeof ResizeObserver === 'function' && previewBar) {
      var previewBarResizeObserver = new ResizeObserver(fitCenteredOffer);
      previewBarResizeObserver.observe(previewBar);
    }
  }

  window.cuStepAfterAddToCart = function (formData, response, drawer, trigger) {
    if (!drawer || drawer.tagName !== 'CART-DRAWER') return false;
    // Keep concurrent additions visible in the cart without opening another offer.
    if (offerBusy || activeAddConfig || (refs.root && refs.root.classList.contains('cu-root-visible'))) {
      drawer.renderContents(response, { open: false });
      return true;
    }

    var nextConfig = resolveAddedProductConfig(formData, response);
    setRuntimeConfig(nextConfig);
    var canShow = CONFIG.enabled && hasConfiguredProducts(CONFIG) && getPreferredUiVariant() === 'upsell2';
    if (canShow && CONFIG.oncePerSession) {
      try { canShow = sessionStorage.getItem(getSessionKey()) !== '1'; } catch (_) {}
    }
    if (!canShow) {
      applyResolvedConfig();
      return false;
    }

    activeAddConfig = nextConfig;
    window.__cuStepCheckoutContext = null;
    try {
      // Update the added items and checkout form without flashing the cart drawer.
      drawer.renderContents(response, { open: false });
      // Resolve the selected offer before revealing the shared popup's contents.
      preloadVariantProduct('upsell2').then(function (product) {
        if (activeAddConfig !== nextConfig) return;
        if (!product || !product.variants || !product.variants.length || !showPanel()) {
          activeAddConfig = null;
          drawer.open(trigger);
          return;
        }
        drawerWasOpen = true;
        previousFocus = trigger || previousFocus;
      }).catch(function (error) {
        if (activeAddConfig !== nextConfig) return;
        activeAddConfig = null;
        console.error('[Upsell Step] unable to open added-product offer:', error);
        drawer.open(trigger);
      });
      return true;
    } catch (error) {
      activeAddConfig = null;
      console.error('[Upsell Step] unable to open added-product offer:', error);
      return false;
    }
  };

  window.cuStepShouldShowOnCheckout = function () {
    applyResolvedConfig();
    return CONFIG.enabled && hasConfiguredProducts(CONFIG) && getPreferredUiVariant() === 'upsell1';
  };

  window.cuStepResolveConfig = resolveEffectiveConfig;

  window.cuStepShow = function () {
    applyResolvedConfig();
    if (getPreferredUiVariant() !== 'upsell1' || !showPanel()) {
      proceedToCheckout();
    }
  };

  function init() {
    setRuntimeConfig(resolveEffectiveConfig());
    setActiveUiVariant(getPreferredUiVariant());

    if (!refs.root || !refs.step) return;

    if (isVariantEnabled('upsell1')) {
      preloadVariantProduct('upsell1');
    }

    if (isVariantEnabled('upsell2')) {
      preloadVariantProduct('upsell2');
    }

    bindEvents();

    if (window.__cuStepPendingOpen) {
      window.__cuStepPendingOpen = false;
      window.cuStepShow();
    }
  }

  document.addEventListener('shopify:section:load', function () {
    activeAddConfig = null;
    applyResolvedConfig();
    if (!CONFIG.enabled || !hasConfiguredProducts(CONFIG)) {
      hidePanel();
      return;
    }
    setActiveUiVariant(getPreferredUiVariant());
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
